
/**
 * Module dependencies.
 */

import { ProductFunction } from 'src/types/products';
import { availableFiltersKeys } from 'src/core/collections-config/filters';
import { functions } from 'src/core/collections-config/functions';
import { getKeyFromObject } from 'src/core/utils/objects';
import { units } from 'src/styles/utils';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import FilterDisclosure from 'src/components/filters/filter-disclosure';
import FilterItem from 'src/components/filters/filter-item';
import FilterItemLabel from 'src/components/filters/filter-item-label';
import React, { ReactElement, useMemo } from 'react';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import split from 'lodash/split';
import styled from 'styled-components';

/**
 * Filter name.
 */

const filterName = availableFiltersKeys.function;

/**
 * `Props` type.
 */

type Props = {
  filtersList?: ProductFunction[]
};

/**
 * `StyledFilterItem` styled component.
 */

const StyledFilterItem = styled(FilterItem)`
  padding: ${units(3)} 0 ${units(3)} ${units(2)} !important;
`;

/**
 * `FunctionFilter` component.
 */

const FunctionFilter = ({ filtersList }: Props): ReactElement => {
  const { translate } = useTranslate();
  const { filters } = useFiltersList();
  const hasFilter = keys(filters).includes(filterName);
  const selectedFilters = hasFilter ? split(filters[filterName], ',').map(functionName => {
    const functionKey = getKeyFromObject(functions, functionName);

    return translate(`products:functions.${functionKey}`);
  }) : [];

  const functionsList = useMemo(() => {
    const functionsValues = Object.values(functions);

    if (isEmpty(filtersList)) {
      return functionsValues;
    }

    return filter(functionsValues, func => includes(filtersList, func));
  }, [filtersList]);

  return (
    <FilterDisclosure
      name={filterName}
      selectedFilters={selectedFilters}
      title={translate('common:labels.function')}
    >
      {functionsList.map((functionValue: string, index: number) => (
        <StyledFilterItem
          key={index}
          label={(
            <FilterItemLabel
              title={translate(`products:functions.${getKeyFromObject(functions, functionValue)}`)}
            />
          )}
          name={filterName}
          value={functionValue}
        />
      ))}
    </FilterDisclosure>
  );
};

/**
 * Export `FunctionFilter` component.
 */

export default FunctionFilter;
